/**
 * TanStack Query client.
 *
 * Tuned for a desk tool that people keep open all day: data is considered fresh
 * briefly, refetches when the operator comes back to the tab, and never retries a
 * request that failed because of permissions or a missing record.
 */
import { QueryClient } from "@tanstack/solid-query";
import { ApiError } from "~/core/api/errors";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Short but non-zero: a tab switch should not restorm the API, while the
      // queue still feels live (list screens set their own refetchInterval).
      staleTime: 10_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        // 401 is handled by the client's refresh-and-replay; 403/404/422 will never
        // succeed on retry. Only retry genuine transient failures.
        if (error instanceof ApiError) {
          if (error.status === 401 || error.isForbidden || error.isNotFound || error.isValidation) {
            return false;
          }
        }
        return failureCount < 2;
      },
    },
    mutations: {
      // A mutation is a deliberate operator action — never silently repeat it.
      retry: false,
    },
  },
});

/**
 * Query keys, centralised so invalidation after a mutation cannot drift from the
 * key a list actually uses. Always build keys from here.
 */
export const qk = {
  me: () => ["me"] as const,

  transactions: {
    all: () => ["transactions"] as const,
    list: (filters: unknown) => ["transactions", "list", filters] as const,
    detail: (id: string) => ["transactions", "detail", id] as const,
    stats: () => ["transactions", "stats"] as const,
    audit: (id: string) => ["transactions", "audit", id] as const,
  },
  chat: {
    all: () => ["chat"] as const,
    rooms: (filters: unknown) => ["chat", "rooms", filters] as const,
    room: (id: string) => ["chat", "room", id] as const,
    messages: (id: string) => ["chat", "messages", id] as const,
    /** Under `chat`, so every `chat:message` invalidation refreshes the nav badge too. */
    unread: () => ["chat", "unread"] as const,
    assignees: () => ["chat", "assignees"] as const,
  },
  referrals: {
    all: () => ["referrals"] as const,
    list: (filters: unknown) => ["referrals", "list", filters] as const,
    stats: () => ["referrals", "stats"] as const,
  },
  dashboard: {
    overview: () => ["dashboard", "overview"] as const,
    activity: () => ["dashboard", "activity"] as const,
  },
  metrics: {
    /** Presence + funnel from `/admin/metrics/overview`, keyed by the picked range. */
    overview: (range: unknown) => ["metrics", "overview", range] as const,
  },
  users: {
    all: () => ["users"] as const,
    list: (filters: unknown) => ["users", "list", filters] as const,
    detail: (id: string) => ["users", "detail", id] as const,
    stats: () => ["users", "stats"] as const,
  },
  markets: {
    all: () => ["markets"] as const,
    list: (filters: unknown) => ["markets", "list", filters] as const,
    detail: (id: string) => ["markets", "detail", id] as const,
    stats: () => ["markets", "stats"] as const,
    rateHistory: (id: string) => ["markets", "rateHistory", id] as const,
    analytics: (id: string) => ["markets", "analytics", id] as const,
    /** Per-market points config + receiving accounts, split by `kind`. */
    config: (id: string, kind: "points" | "receiving") =>
      ["markets", "config", id, kind] as const,
  },
  pricing: {
    all: () => ["pricing"] as const,
    groups: () => ["pricing", "groups"] as const,
    group: (id: string) => ["pricing", "group", id] as const,
    rules: () => ["pricing", "rules"] as const,
    runs: (filters: unknown) => ["pricing", "runs", filters] as const,
    run: (id: string) => ["pricing", "run", id] as const,
  },
  referenceRates: {
    all: () => ["reference-rates"] as const,
    sources: () => ["reference-rates", "sources"] as const,
    source: (id: string) => ["reference-rates", "source", id] as const,
    latest: (sourceKey: string) => ["reference-rates", "latest", sourceKey] as const,
    history: (filters: unknown) => ["reference-rates", "history", filters] as const,
  },
  audit: {
    all: () => ["audit"] as const,
    list: (filters: unknown) => ["audit", "list", filters] as const,
    detail: (id: string) => ["audit", "detail", id] as const,
    entity: (entity_type: string, entity_id: string) =>
      ["audit", "entity", entity_type, entity_id] as const,
  },
  campaigns: {
    all: () => ["campaigns"] as const,
    list: (filters: unknown) => ["campaigns", "list", filters] as const,
    detail: (id: string) => ["campaigns", "detail", id] as const,
  },
  verifications: {
    all: () => ["verifications"] as const,
    list: (filters: unknown) => ["verifications", "list", filters] as const,
    detail: (id: string) => ["verifications", "detail", id] as const,
    calls: (filters: unknown) => ["verifications", "calls", filters] as const,
  },
  promotions: {
    all: () => ["promotions"] as const,
    list: (filters: unknown) => ["promotions", "list", filters] as const,
    detail: (id: string) => ["promotions", "detail", id] as const,
  },
  challenges: {
    all: () => ["challenges"] as const,
    list: (filters: unknown) => ["challenges", "list", filters] as const,
    detail: (id: string) => ["challenges", "detail", id] as const,
  },
  staff: {
    all: () => ["staff"] as const,
    list: () => ["staff", "list"] as const,
    detail: (id: string) => ["staff", "detail", id] as const,
    roles: () => ["staff", "roles"] as const,
    permissions: () => ["staff", "permissions"] as const,
  },
  bankAccounts: {
    all: () => ["bank-accounts"] as const,
    list: () => ["bank-accounts", "list"] as const,
  },
  system: {
    config: () => ["system", "config"] as const,
    versions: () => ["system", "versions"] as const,
  },
} as const;
